import { AxiosInstance } from "axios"
import {
    CreateUpdateMerchant,
    Merchant,
    MerchantBeginOnboardingContent,
    MerchantDetails,
    MerchantSettingsOnboardingArticle,
} from "./supplierMerchantsTypes"
import { ServiceResponse } from "../../../../../core/api/apiTypes"

export enum SupplierMerchantQuery {
    LIST = "merchants-for-supplier",
    DETAILS = "merchant-details-for-supplier",
    MERCHANT_BEGIN_ONBOARDING_ARTICLES = "merchant-begin-onboarding-articles",
}

type SupplierMerchantsParams = Readonly<{
    supplierId: number
}>

export type SupplierMerchantsResponse = Readonly<{
    merchants: Merchant[]
}>

export async function fetchSupplierMerchants(
    { supplierId }: SupplierMerchantsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierMerchantsResponse> {
    const url = `/suppliers/${supplierId}/merchants`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

type SupplierMerchantDetailsParams = Readonly<{
    merchantId: string | number
}>

export type SupplierMerchantDetailsResponse = Readonly<{
    merchant: MerchantDetails
}>

export async function fetchSupplierMerchantDetails(
    { merchantId }: SupplierMerchantDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierMerchantDetailsResponse> {
    const url = `/merchants/${merchantId}`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

type SupplierCreateMerchantParams = Readonly<{
    supplierId: number
    merchant: CreateUpdateMerchant
}>

export async function createMerchant(
    { supplierId, merchant }: SupplierCreateMerchantParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/merchants`,
        {
            merchant: {
                supplier: {
                    id: supplierId,
                },
                ...merchant,
            },
        },
        {
            signal,
        },
    )
    return data
}

type SupplierEditMerchantParams = Readonly<{
    merchant: CreateUpdateMerchant
    merchantId: string
}>

export async function updateMerchant(
    { merchant, merchantId }: SupplierEditMerchantParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/merchants/${merchantId}`,
        {
            merchant: {
                ...merchant,
            },
        },
        {
            signal,
        },
    )
    return data
}

type DeleteMerchantParam = Readonly<{
    merchantId: number
}>
export async function deleteMerchant(
    { merchantId }: DeleteMerchantParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.delete(`/merchants/${merchantId}`, {
        signal,
    })

    return data
}

type MerchantSettingsOnboardingArticlesParams = Readonly<{
    merchantId: number
}>

export type MerchantSettingsOnboardingArticlesResponse = Readonly<{
    onboardingArticles: MerchantSettingsOnboardingArticle[]
}>

export async function fetchMerchantSettingsOnboardingArticles(
    { merchantId }: MerchantSettingsOnboardingArticlesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MerchantSettingsOnboardingArticlesResponse> {
    const url = `/merchants/${merchantId}/settings/onboarding/articles`

    const { data } = await axios.get(url, { signal })

    return data
}

type MerchantBeginOnboardingParam = Readonly<{
    merchantId: number
    content: MerchantBeginOnboardingContent
}>

export async function merchantBeginOnboarding(
    { merchantId, content }: MerchantBeginOnboardingParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `merchants/${merchantId}/subscription`,
        {
            ...content,
        },
        {
            signal,
        },
    )
    return data
}
